import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { AuditChip } from '@/components/audit/AuditChip';
import { runDsarPurgeGrace } from '@/api/admin/dsar';
import { qk } from '@/queries/queryKeys';
import { useAuthStore } from '@/stores/auth';
import { useUiStore, type Locale, type Theme } from '@/stores/ui';
import { useApiError } from '@/lib/useApiError';
import { maskUuid } from '@/lib/mask';

export const Route = createFileRoute('/_authed/settings')({
  component: SettingsScreen,
});

const LOCALES: readonly Locale[] = ['en', 'ar', 'eg-ar'];
const THEMES: readonly Theme[] = ['light', 'dark', 'system'];

function SettingsScreen() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const userId = useAuthStore((s) => s.userId);
  const locale = useUiStore((s) => s.locale);
  const theme = useUiStore((s) => s.theme);
  const setLocale = useUiStore((s) => s.setLocale);
  const setTheme = useUiStore((s) => s.setTheme);
  const qc = useQueryClient();
  const resolveError = useApiError();

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const purgeMutation = useMutation({
    mutationFn: () => runDsarPurgeGrace(),
    onSuccess: (data) => {
      toast.success(t('dsar.successTitle'), {
        description: t('dsar.successBody', { count: data.purged }),
      });
      void qc.invalidateQueries({ queryKey: qk.audit() });
    },
    onError: (e) => {
      const resolved = resolveError(e);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    },
  });

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('profile.title')}</CardTitle>
          <CardDescription>{t('profile.body')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="space-y-2 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-[color:var(--color-fg-muted)]">{t('profile.userId')}</dt>
              <dd className="font-mono text-xs" dir="ltr">
                {userId ? maskUuid(userId) : '—'}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-[color:var(--color-fg-muted)]">{t('profile.scope')}</dt>
              <dd className="flex items-center gap-2">
                <Badge tone="success">{t('profile.adminBadge')}</Badge>
                <Badge tone="accent">{t('profile.mfaBadge')}</Badge>
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('preferences.title')}</CardTitle>
          <CardDescription>{t('preferences.body')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <p className="mb-2 text-sm font-medium">{t('preferences.theme')}</p>
            <div role="radiogroup" aria-label={t('preferences.theme')} className="flex flex-wrap gap-2">
              {THEMES.map((opt) => {
                const checked = theme === opt;
                return (
                  <label
                    key={opt}
                    className={`cursor-pointer rounded-[var(--radius-pill)] border px-3 py-1.5 text-xs font-medium ${
                      checked
                        ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]'
                        : 'border-[color:var(--color-border)]'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={checked}
                      onChange={() => setTheme(opt)}
                      className="sr-only"
                    />
                    {tc(`userMenu.theme${opt[0]?.toUpperCase()}${opt.slice(1)}`)}
                  </label>
                );
              })}
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium">{t('preferences.locale')}</p>
            <div role="radiogroup" aria-label={t('preferences.locale')} className="flex flex-wrap gap-2">
              {LOCALES.map((opt) => {
                const checked = locale === opt;
                return (
                  <label
                    key={opt}
                    className={`cursor-pointer rounded-[var(--radius-pill)] border px-3 py-1.5 text-xs font-medium ${
                      checked
                        ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)]'
                        : 'border-[color:var(--color-border)]'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={checked}
                      onChange={() => setLocale(opt)}
                      className="sr-only"
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base">{t('dsar.title')}</CardTitle>
              <CardDescription>{t('dsar.body')}</CardDescription>
            </div>
            <AuditChip />
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button
            variant="danger"
            onClick={() => setConfirmOpen(true)}
            loading={purgeMutation.isPending}
          >
            <Trash2 className="size-4" aria-hidden />
            {t('dsar.cta')}
          </Button>
          <p className="text-xs text-[color:var(--color-fg-muted)]">{t('dsar.cadence')}</p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('dsar.confirmTitle')}
        description={t('dsar.confirmBody')}
        destructive
        requireType="PURGE"
        confirmLabel={t('dsar.cta')}
        loading={purgeMutation.isPending}
        onConfirm={async () => {
          setConfirmOpen(false);
          await purgeMutation.mutateAsync();
        }}
      />
    </div>
  );
}
